"use client";
import { useState, useEffect } from "react";
import { STRAPI_URL, GETINSTANCEINFO_URL } from "@/global_variables";

interface StrapiResponse<T = any> {
  data: T;
  meta?: Record<string, any>;
}

interface UseStrapiReturn<T = any> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

// Cache for instance info with 0.5 minute TTL
let instanceInfoCache: any = null;
let instanceInfoCacheTime = 0;
let instanceInfoPromise: Promise<any> | null = null;
const CACHE_TTL = 0.5 * 60 * 1000; // 0.5 minute in milliseconds

export const getInstanceInfoCached = async (): Promise<any> => {
  const now = Date.now();

  // Return cached result if still valid
  if (instanceInfoCache && now - instanceInfoCacheTime < CACHE_TTL) {
    return instanceInfoCache;
  }

  // Deduplicate concurrent calls across multiple hook instances
  if (instanceInfoPromise) {
    return instanceInfoPromise;
  }

  instanceInfoPromise = (async () => {
    try {
      const response = await fetch(GETINSTANCEINFO_URL);
      const data = await response.json();
      instanceInfoCache = data;
      instanceInfoCacheTime = Date.now();
      return data;
    } catch (error) {
      // Return cached data even if expired, or fallback to OFFLINE
      if (instanceInfoCache) {
        return instanceInfoCache;
      }
      return { status: "OFFLINE" };
    } finally {
      instanceInfoPromise = null;
    }
  })();

  return instanceInfoPromise;
};

const processMediaFields = (obj: any, url: string, imageSize: string): any => {
  if (!obj || typeof obj !== "object") return obj;

  const processed = { ...obj };
  for (const [key, value] of Object.entries(processed)) {
    if (
      value &&
      typeof value === "object" &&
      "url" in value &&
      "mime" in value
    ) {
      let imageUrl = (value as any).formats?.[imageSize]?.url;

      if (!imageUrl) {
        // If no formats are available, use the original URL
        const originalUrl = (value as any).url;

        if (url === "") {
          imageUrl = originalUrl.replace(
            /(^|\/)([^/]+)$/,
            (_: string, slash: string, filename: string) =>
              `${slash}large_${filename}`,
          );
        } else {
          imageUrl = originalUrl;
        }
      }

      // Replace media object with just the URL (prefer large format)
      (processed as any)[key] = `${url}${imageUrl}`;
    }
  }
  return processed;
};

export function useStrapi<T = any>(
  tableName: string,
  imageSize: string = "large",
): UseStrapiReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Create abort controller with 1 second timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 6000);
        let response: Response;
        let url: string;
        try {
          // Get instance info (cached)
          const instanceInfo = await getInstanceInfoCached();

          try {
            if (instanceInfo.status === "RUNNING") {
              response = await fetch(
                `${STRAPI_URL}/api/${tableName}?populate=*`,
                {
                  signal: controller.signal,
                },
              );
              url = STRAPI_URL;
            } else {
              throw new Error("Instance is not running");
            }
          } catch (err) {
            response = await fetch(`/api-snapshot/${tableName}.json`);
            url = "";
          }

          if (!response.ok) {
            throw new Error(
              `Failed to fetch ${tableName}: ${response.statusText}`,
            );
          }
          const result: StrapiResponse<T> = await response.json();

          // Process data to convert media objects with url and mime to just url
          let processedData: T = result.data;

          if (result.data && Array.isArray(result.data)) {
            processedData = result.data.map((item: any) =>
              processMediaFields(item, url, imageSize),
            ) as unknown as T;
          } else if (result.data && !Array.isArray(result.data)) {
            processedData = processMediaFields(result.data, url, imageSize);
          }

          setData(processedData);
          setError(null);
        } finally {
          clearTimeout(timeoutId);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"));
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tableName]);

  return { data, loading, error };
}
